import type { GroupAnalysisResponse } from '../../api/types.js';

export type DetailSelectionEvent =
  | { type: 'selected'; id: number }
  | { type: 'cleared' }
  | { type: 'loaded'; id: number; analysis: GroupAnalysisResponse }
  | { type: 'load_failed'; id: number; message: string };

/**
 * 分析详情的选中与请求序号门闩：只接纳最后一次请求的结果；
 * clear() 同样使序号失效——清空选中（返回列表/切换筛选）后，进行中的详情请求
 * 返回时不得再写入已清空的选中状态。
 */
export function createDetailSelection(deps: {
  load(id: number): Promise<GroupAnalysisResponse>;
  emit(event: DetailSelectionEvent): void;
}) {
  let seq = 0;
  let selectedId: number | null = null;
  const request = (id: number) => {
    const token = ++seq;
    void deps.load(id).then(
      (analysis) => { if (token === seq) deps.emit({ type: 'loaded', id, analysis }); },
      (error) => {
        if (token === seq) deps.emit({ type: 'load_failed', id, message: error instanceof Error ? error.message : '分析详情加载失败' });
      },
    );
  };
  return {
    select(id: number): void {
      selectedId = id;
      deps.emit({ type: 'selected', id });
      request(id);
    },
    /** 轮询刷新：不重置选中 UI，但同样受序号门闩约束（期间 clear 后结果被丢弃）。 */
    refresh(id: number): void {
      if (selectedId !== id) return;
      request(id);
    },
    isSelected(id: number): boolean { return selectedId === id; },
    clear(): void {
      selectedId = null;
      seq += 1;
      deps.emit({ type: 'cleared' });
    },
  };
}

export type DetailSelection = ReturnType<typeof createDetailSelection>;
